import {
  Component,
  ElementRef,
  EventEmitter, HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output, QueryList,
  ViewChildren
} from '@angular/core';
import {animationFrameScheduler, Subscription} from 'rxjs';
import {finalize} from 'rxjs/operators';

import {
  GetRegion2Css,
  GetRegion2Name, DEFAULT_SCROLL_SPEED
} from '../../defines/client.const';
import {POOMSA, POOMSA_DISP_LISP} from '../../defines/gukeo.const';
import {hunBody, hunChar} from '../../defines/gukeo.func';
import {Word} from '../../models/word.model';
import {VoiceService} from '../../services/voice/voice.service';
import {WordBookmarkService} from '../../services/word/word-bookmark.service';
import {WordService} from '../../services/word/word.service';
import {OptionService} from '../../services/option/option.service';
import {ScrollDownService} from "../../services/scroll-down/scroll-down.service";

const HOMONYM_FULL_WORD_STRUCT_LENGTH = 6;

@Component({
  selector: 'app-word-detail',
  templateUrl: './word-detail.component.html',
  styleUrls: ['./word-detail.component.scss']
})
export class WordDetailComponent implements OnInit, OnDestroy {
  @HostBinding('style.display') display = 'block';
  @ViewChildren('listHomonym', {read: ElementRef}) listHomonyms: QueryList<ElementRef>;
  @Input() word_id: string;
  @Input() height: number;
  @Output() closeDetail: EventEmitter<boolean>;
  @Output() openMap: EventEmitter<Word>;

  protected readonly hunBody = hunBody;
  protected readonly hunChar = hunChar;
  protected readonly GetRegion2Css = GetRegion2Css;
  protected readonly GetRegion2Name = GetRegion2Name;

  word: Word;
  private sendingBookmark = false;

  private playWord$: Subscription = null;

  // 푸터의 아래로 내리기 버튼 구독
  private scrollDown$: Subscription | null = null;

  // 이운이의어 부분에 디자인을 1컬럼으로 변경할지 여부 속성.
  public classFull = false;
  public showExampleLongVowels = true;

  constructor(
    private container: ElementRef,
    private wordSvc: WordService,
    private option: OptionService,
    private voice: VoiceService,
    private bmSvc: WordBookmarkService,
    private scrollDownSvc: ScrollDownService,
  ) {
    this.closeDetail = new EventEmitter<boolean>();
    this.openMap = new EventEmitter<Word>();
  }

  ngOnInit(): void {
    // console.log('WordDetailComponent word=', this.word);
    this.wordSvc.GetDetail(this.word_id).subscribe(
      data => {
        console.log(data);
        this.word = data;

        if (this.option.autoPlay) {
          this.playWord$ = this.voice.PlayWord(this.word).pipe(
            finalize(() => this.playWord$ = null)
          ).subscribe();
        }

        this.updateHomonymClassFull();

        // 이운이의어 항목이 로딩된 이후에 크기를 계산해서 2컬럼 디자인이 좁다면 1컬럼 디자인으로 변경한다.
        this.listHomonyms.changes.subscribe((listHomonyms: QueryList<ElementRef>) => {
          animationFrameScheduler.schedule(() => {
            for (let homonym of listHomonyms) {
              const firstChild = homonym.nativeElement.firstChild;
              const homonymWidth = homonym.nativeElement.offsetWidth;
              const firstChildWidth = firstChild.clientWidth;
              if (firstChildWidth > homonymWidth) {
                this.classFull = true;
                break;  // 하나라도 크기가 넘치면 종료.
              }
            }
          });
        });

      },
      err => console.error(err)
    );

    this.scrollDown$ = this.scrollDownSvc.pushScrollDown$().subscribe(isFirst => {
      const scrollDelta = DEFAULT_SCROLL_SPEED * (isFirst ? 2 : 1);  // 스크롤 시작할 때 첫번째는 두배로 스크롤 함.

      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + scrollDelta,
        behavior: 'auto',
      });
    });
  }

  ngOnDestroy(): void {
    if (this.playWord$) {
      this.playWord$.unsubscribe();
      this.playWord$ = null;
    }

    this.scrollDownSvc.popScrollDown$();
    if (this.scrollDown$) {
      this.scrollDown$.unsubscribe();
      this.scrollDown$ = null;
    }
  }

  public onClose(): void {
    console.log('onClose()', this.word.bookmark);
    this.closeDetail.emit(this.word?.bookmark ?? false);
  }

  public ToggleBookmark(event: MouseEvent, word: Word): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.sendingBookmark) {
      return; // 이미 보내고 있다.
    }
    this.sendingBookmark = true;
    word.bookmark = !word.bookmark;
    this.bmSvc.sendBookmark(word.id, word.bookmark).pipe(
      finalize(() => this.sendingBookmark = false)
    ).subscribe(
      data => console.log(data),
      err => console.error(err)
    );
  }

  public ToggleExampleLongVowels(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    this.showExampleLongVowels = !this.showExampleLongVowels;
  }

  public getBmCss(word: Word): string {
    if (this.sendingBookmark) {
      return 'ing';
    }
    return word.bookmark ? 'on' : '';
  }

  public GetPoomsaTxt(poomsa: POOMSA): string {
    return POOMSA_DISP_LISP[poomsa];
  }

  public onOpenMap(word: Word): void {
    this.openMap.emit(this.word);
  }

  private updateHomonymClassFull(): void {
    this.classFull = this.word?.homonyms?.some(
      homonym => (homonym.wordStruct?.length ?? 0) >= HOMONYM_FULL_WORD_STRUCT_LENGTH
    ) ?? false;
  }

}
